/**
 * Typed ops device fleet API facade over the generated contract client.
 */

import { opsDevicesApi } from '../api/generated/opsDevices.api.js';
import type { OpsProviderActivateResponse } from '@cnapse/shared/schemas/ops-devices.schemas.js';
import {
  type InventoryStateTransitionResponse,
  type OpsAllocateDeviceRequest,
  type OpsAllocateDeviceResponse,
  type OpsAllowReprovisionResponse,
  type OpsAuditLogResponse,
  type OpsBulkAllocateRequest,
  type OpsBulkAllocateResponse,
  type OpsDeviceDetailResponse,
  type OpsDeviceListQuery,
  type OpsDeviceListResponse,
  type OpsFleetStatsResponse,
  type OpsIotRevokeCertificateResponse,
  type OpsRebootDeviceResponse,
  type OpsResetDeviceResponse,
  type OpsRevealBreakGlassPasswordResponse,
  type OpsTriggerReprovisionResponse,
  type OpsUnallocateDeviceResponse,
  type RegenerateClaimCodeResponse,
} from '@cnapse/shared/schemas.js';

class OpsDevicesService {
  async getFleetStats(): Promise<OpsFleetStatsResponse> {
    return opsDevicesApi.getFleetStats() as Promise<OpsFleetStatsResponse>;
  }

  async listDevices(query: Partial<OpsDeviceListQuery> = {}): Promise<OpsDeviceListResponse> {
    return opsDevicesApi.list(query) as Promise<OpsDeviceListResponse>;
  }

  async getDevice(serialNumber: string): Promise<OpsDeviceDetailResponse> {
    return opsDevicesApi.getBySerial(serialNumber) as Promise<OpsDeviceDetailResponse>;
  }

  async getDeviceAuditLog(serialNumber: string): Promise<OpsAuditLogResponse> {
    return opsDevicesApi.getAuditLog(serialNumber) as Promise<OpsAuditLogResponse>;
  }

  async allocateDevice(
    serialNumber: string,
    data: OpsAllocateDeviceRequest,
  ): Promise<OpsAllocateDeviceResponse> {
    return opsDevicesApi.allocate(serialNumber, data) as Promise<OpsAllocateDeviceResponse>;
  }

  async unallocateDevice(serialNumber: string, reason: string): Promise<OpsUnallocateDeviceResponse> {
    return opsDevicesApi.unallocate(serialNumber, { reason }) as Promise<OpsUnallocateDeviceResponse>;
  }

  async decommissionDevice(serialNumber: string, reason: string): Promise<OpsIotRevokeCertificateResponse> {
    return opsDevicesApi.revokeCertificate(serialNumber, { reason }) as Promise<OpsIotRevokeCertificateResponse>;
  }

  async resetDevice(serialNumber: string): Promise<OpsResetDeviceResponse> {
    return opsDevicesApi.reset(serialNumber) as Promise<OpsResetDeviceResponse>;
  }

  async bulkAllocateDevices(data: OpsBulkAllocateRequest): Promise<OpsBulkAllocateResponse> {
    return opsDevicesApi.bulkAllocate(data) as Promise<OpsBulkAllocateResponse>;
  }

  async markDeviceShipped(serialNumber: string): Promise<InventoryStateTransitionResponse> {
    return opsDevicesApi.markShipped(serialNumber, {}) as Promise<InventoryStateTransitionResponse>;
  }

  async activateDevice(serialNumber: string): Promise<OpsProviderActivateResponse> {
    return opsDevicesApi.providerActivate({ serialNumber }) as Promise<OpsProviderActivateResponse>;
  }

  async returnDevice(serialNumber: string, reason: string): Promise<InventoryStateTransitionResponse> {
    return opsDevicesApi.returnDevice(serialNumber, { reason }) as Promise<InventoryStateTransitionResponse>;
  }

  async retireDevice(serialNumber: string, reason: string): Promise<InventoryStateTransitionResponse> {
    return opsDevicesApi.retireDevice(serialNumber, { reason }) as Promise<InventoryStateTransitionResponse>;
  }

  async regenerateClaimCode(serialNumber: string): Promise<RegenerateClaimCodeResponse> {
    return opsDevicesApi.regenerateClaimCode(serialNumber, {}) as Promise<RegenerateClaimCodeResponse>;
  }

  async registerIotDevice(body: {
    serialNumber?: string;
    vendorSerialNumber: string;
    ekCertPem: string;
    akCertPem: string;
    manufacturerName?: string;
    model: string;
    mfgBatch: string;
    mfgDate: string;
    deviceCategory?: 'efem-gateway' | 'location-gateway';
    macAddress?: string;
    notes?: string;
  }): Promise<{ success: boolean; message: string; data: { device: Record<string, unknown> } }> {
    return opsDevicesApi.registerIotDevice(body) as Promise<{
      success: boolean;
      message: string;
      data: { device: Record<string, unknown> };
    }>;
  }

  async registerNonIotDevice(body: {
    serialNumber?: string;
    vendorSerialNumber: string;
    macAddress: string;
    deviceCategory: string;
    manufacturerName?: string;
    model: string;
    mfgBatch: string;
    mfgDate: string;
    vendorDeviceId?: string;
    notes?: string;
  }): Promise<{ success: boolean; message: string; data: { device: Record<string, unknown> } }> {
    return opsDevicesApi.registerNonIotDevice(body) as Promise<{
      success: boolean;
      message: string;
      data: { device: Record<string, unknown> };
    }>;
  }

  async updateNonIotDevice(
    serialNumber: string,
    body: {
      serialNumber?: string;
      deviceCategory?: string;
      manufacturerName?: string;
      model?: string;
      mfgBatch?: string;
      mfgDate?: string;
      macAddress?: string;
      vendorSerialNumber?: string;
      vendorDeviceId?: string;
      notes?: string;
      akCertPem?: string;
      efemPeripheral?: Record<string, unknown> | null;
    },
  ): Promise<{ success: boolean; data: { device: unknown } }> {
    return opsDevicesApi.updateNonIotDevice({ serialNumber }, body) as Promise<{
      success: boolean;
      data: { device: unknown };
    }>;
  }

  async providerActivateDevice(serialNumber: string): Promise<{ success: boolean }> {
    const response = (await opsDevicesApi.providerActivate(serialNumber)) as { success: boolean };
    return { success: response.success };
  }

  async allowDeviceReprovisioning(
    serialNumber: string,
    reason: string,
  ): Promise<OpsAllowReprovisionResponse> {
    return opsDevicesApi.allowReprovisioning(serialNumber, { reason }) as Promise<OpsAllowReprovisionResponse>;
  }

  async triggerDeviceReprovisioning(
    serialNumber: string,
    reason: string,
  ): Promise<OpsTriggerReprovisionResponse> {
    return opsDevicesApi.triggerReprovisioning(serialNumber, { reason }) as Promise<OpsTriggerReprovisionResponse>;
  }

  async rebootDevice(serialNumber: string, reason: string): Promise<OpsRebootDeviceResponse> {
    return opsDevicesApi.reboot(serialNumber, { reason }) as Promise<OpsRebootDeviceResponse>;
  }

  async revealBreakGlassPassword(
    serialNumber: string,
    reason: string,
  ): Promise<OpsRevealBreakGlassPasswordResponse> {
    return opsDevicesApi.revealBreakGlassPassword(serialNumber, { reason }) as Promise<OpsRevealBreakGlassPasswordResponse>;
  }
}

export const opsDevicesService = new OpsDevicesService();
